import { useSyncExternalStore } from 'react';

/**
 * Three languages, keyed by the English sentence.
 *
 * Keyed by the sentence rather than by `home.title.greeting`, because the
 * screens read as English when nobody has translated a line yet, and a missing
 * translation degrades to something true instead of to a key nobody can parse
 * on a factory floor.
 *
 * `{name}` in a sentence is replaced from the second argument. Nothing else is
 * supported on purpose: plurals in three languages need a library, and every
 * count here is written to read the same either way.
 */
export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'es', label: 'Español' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];

const KEY = 'mealdesk.language';

const KO: Record<string, string> = {
  // Meals, as the shared constants spell them
  Breakfast: '아침',
  Lunch: '점심',
  Dinner: '저녁',
  // Shell
  Home: '홈',
  Menu: '메뉴',
  Orders: '주문',
  Alerts: '알림',
  // Home
  'Hi, {name}': '{name}님, 안녕하세요',
  Hi: '안녕하세요',
  'Nothing open to order': '지금 주문할 수 있는 날이 없습니다',
  'Every published day has closed. The weekly menu shows what is coming.':
    '올라온 날이 모두 마감됐습니다. 주간 메뉴에서 다음 주를 볼 수 있습니다.',
  'Collecting today': '오늘 받을 식사',
  'No pickup point set': '수령 장소가 지정되지 않았습니다',
  'Order for {day}': '{day} 주문',
  'Nothing to order': '주문할 것이 없습니다',
  'No open days': '주문 가능한 날 없음',
  'Everything published has closed. The weekly menu shows what is coming.':
    '올라온 날이 모두 마감됐습니다. 주간 메뉴를 확인하세요.',
  'Whole week': '주간 전체',
  'Booked ahead': '미리 신청함',
  'left · closes {when}': '남음 · {when} 마감',
  Ordered: '신청함',
  'View or change': '보기 · 변경',
  'Order {meal}': '{meal} 신청',
  // Menu
  'Weekly menu': '주간 메뉴',
  'days published': '일 공개됨',
  'This week': '이번 주',
  'Book several days': '여러 날 신청',
  Cancel: '취소',
  'Tap the days you want. You choose where they go on the next screen — all together, or one day at a time.':
    '원하는 날을 선택하세요. 받을 장소는 다음 화면에서 정합니다 — 전체를 한 번에, 또는 하루씩.',
  Open: '신청 가능',
  Closed: '마감',
  'Order by {when}': '{when}까지 신청',
  'Closed {when}': '{when} 마감됨',
  Continue: '다음',
  'Nothing published yet': '아직 공개된 메뉴가 없습니다',
  'Nothing is published for these days yet. Your restaurant posts the week when it is ready.':
    '이 기간에 공개된 메뉴가 없습니다. 식당이 준비되면 올립니다.',
  // Batch
  'Where do these go?': '어디서 받으시겠습니까?',
  'Every day': '전체',
  'Choose a pickup point': '수령 장소를 선택하세요',
  'Nothing can be ordered until one is set': '장소를 정해야 신청할 수 있습니다',
  Change: '변경',
  'The days you picked': '선택한 날',
  'No pickup point': '수령 장소 없음',
  'Back to the menu': '메뉴로 돌아가기',
  'Nothing picked': '선택한 날이 없습니다',
  'Choose the days you want on the weekly menu, then set where they go.':
    '주간 메뉴에서 원하는 날을 고른 뒤 받을 장소를 정하세요.',
  'Every day goes to': '전체를 받을 장소',
  'Sets all of them. A single day can still be changed on its own card.':
    '선택한 모든 날에 적용됩니다. 특정 날만 따로 바꿀 수 있습니다.',
  'Just this day': '이 날만',
  'Only this meal moves. The rest stay where they are.':
    '이 식사만 옮겨집니다. 나머지는 그대로입니다.',
  'set to somewhere else': '일은 다른 곳으로 지정됨',
  'See my orders': '내 주문 보기',
  'These could not be ordered:': '신청되지 않은 날:',
  // Order
  'Your share': '본인 부담',
  Billing: '결제',
  'Place order': '신청하기',
  'This meal is closed': '이 식사는 마감됐습니다',
  Allergens: '알레르기 정보',
  'Pickup point': '수령 장소',
  // Orders
  'My orders': '내 주문',
  'Next up': '다음 식사',
  'Collect at {place}': '{place}에서 수령',
  'No pickup point — set one in your profile': '수령 장소 없음 — 프로필에서 지정하세요',
  'Nothing booked': '신청한 식사가 없습니다',
  'Days you order appear here with what you ordered and where to collect it.':
    '신청한 날과 무엇을·어디서 받는지가 여기에 표시됩니다.',
  'Nothing yet this month': '이번 달 내역이 없습니다',
  'Meals you have eaten appear here.': '드신 식사가 여기에 표시됩니다.',
  'company paid in full': '회사 전액 부담',
  Confirmed: '확정',
  Delivered: '배달 완료',
  Cancelled: '취소됨',
  Exception: '예외 처리',
  // Alerts
  'Mark all read': '모두 읽음',
  'Nothing yet': '아직 없습니다',
  'Cutoff reminders and menu updates arrive here.': '마감 알림과 메뉴 소식이 여기에 옵니다.',
  'Email is on by default. Push and SMS come in a later version.':
    '이메일은 기본으로 켜져 있습니다. 푸시와 문자는 다음 버전에 추가됩니다.',
  // Profile
  'Where your meals are delivered. Changing it affects new orders — ones you have already placed keep the point they were made with.':
    '식사를 받는 곳입니다. 변경은 새 주문부터 적용되며, 이미 신청한 건은 그대로입니다.',
  'No pickup points are set up for your site yet. Ask your company admin.':
    '이 사업장에 등록된 수령 장소가 없습니다. 회사 관리자에게 문의하세요.',
  Password: '비밀번호',
  'Change your password': '비밀번호 변경',
  'Sign out': '로그아웃',
  Language: '언어',
  // Sign in
  'Sign in': '로그인',
  'Your work email and the password you chose. You stay signed in for 90 days.':
    '회사 이메일과 설정하신 비밀번호로 로그인합니다. 90일간 유지됩니다.',
  'Work email': '회사 이메일',
  'First time here, or forgot your password?': '처음이신가요? 비밀번호를 잊으셨나요?',
  'Only addresses your company registered can sign in. Guests get a temporary pass for their visit dates.':
    '회사가 등록한 주소만 로그인할 수 있습니다. 방문객은 방문 기간에만 쓰는 임시 계정을 받습니다.',
  'Set your password': '비밀번호 설정',
  'We send a link to your work email. Following it proves the address is yours, and then you choose a password.':
    '회사 이메일로 링크를 보냅니다. 링크를 열면 주소가 본인 것임이 확인되고, 비밀번호를 정하게 됩니다.',
  'Send the link': '링크 보내기',
  'Send another link': '링크 다시 보내기',
  'Back to sign in': '로그인으로 돌아가기',
  'Choose a password': '비밀번호 정하기',
  Again: '다시 입력',
  'New password': '새 비밀번호',
  'Save and sign in': '저장하고 로그인',
  'Choose your own password': '직접 비밀번호를 정하세요',
  'You are signed in with a password somebody gave you. Replace it before carrying on.':
    '전달받은 비밀번호로 로그인한 상태입니다. 계속하시려면 직접 정한 비밀번호로 바꿔주세요.',
  'The password you were given': '전달받은 비밀번호',
  'Save and sign in again': '저장하고 다시 로그인',

  // Joining from the company link
  'This link no longer works':
    '이 링크는 더 이상 쓸 수 없습니다',
  'Your company may have replaced it. Ask whoever manages accounts there for the current one.':
    '회사에서 새 링크로 바꿨을 수 있습니다. 계정 담당자에게 지금 쓰는 링크를 받으세요.',
  'Already have an account? Sign in':
    '이미 계정이 있나요? 로그인',
  'Request sent':
    '가입 신청 완료',
  '{company} will look at it. Once they approve it, sign in with the email and password you just chose.':
    '{company}에서 확인합니다. 승인되면 방금 정한 이메일과 비밀번호로 로그인하세요.',
  'Nothing else to do. If it is urgent, tell whoever manages accounts at your company that you have asked.':
    '더 할 일은 없습니다. 급하면 회사 계정 담당자에게 신청했다고 알려주세요.',
  'Could not sign you up':
    '가입하지 못했습니다',
  'Could not sign you in':
    '로그인하지 못했습니다',
  'Join {company}':
    '{company} 가입',
  'Join your company':
    '회사에 가입',
  'Sign up and you can order straight away.':
    '가입하면 바로 주문할 수 있습니다.',
  'If your employee number and email match what your company has on file, you can order straight away. If not, your company approves you first.':
    '사번과 이메일이 회사 명부와 맞으면 바로 주문할 수 있습니다. 맞지 않으면 회사가 먼저 승인합니다.',
  'Employee number':
    '사번',
  'The number on your badge. Leave it blank if you do not have one.':
    '사원증에 있는 번호입니다. 없으면 비워두세요.',
  'Email':
    '이메일',
  'The one your company has for you, if it has one.':
    '회사에 등록된 이메일이 있으면 그걸 쓰세요.',
  'Name':
    '이름',
  'At least 10 characters. You stay signed in for 90 days.':
    '10자 이상. 90일 동안 로그인이 유지됩니다.',
  'Where do you pick up lunch?':
    '어디서 식사를 받나요?',
  'Decide later':
    '나중에 정하기',
  'Join':
    '가입하기',
  'Your company has not approved this account yet. You can sign in as soon as it does.':
    '아직 회사가 이 계정을 승인하지 않았습니다. 승인되는 대로 로그인할 수 있습니다.',
  'That email is already registered. Sign in, or ask whoever manages accounts at your company.':
    '이미 등록된 이메일입니다. 로그인하거나 회사 계정 담당자에게 문의하세요.',
  'This link is not valid':
    '유효하지 않은 링크입니다',
  'That pickup point is not one of this company’s':
    '이 회사의 수령 장소가 아닙니다',
  'Password must be at least 10 characters':
    '비밀번호는 10자 이상이어야 합니다',

  // Order limits
  'Full':
    '마감(수량)',
  '{count} left':
    '{count}개 남음',
  'The kitchen has taken all it can cook for this day.':
    '이 날 조리할 수 있는 수량이 모두 찼습니다.',
  'Replace order':
    '주문 바꾸기',

  // Cancelling, and the order screen
  'Cancel order':
    '주문 취소',
  'Cancel this meal?':
    '이 식사를 취소할까요?',
  'You can order it again until {when}.':
    '{when}까지는 다시 신청할 수 있습니다.',
  'Yes, cancel':
    '네, 취소합니다',
  'Keep it':
    '그대로 두기',
  'Cancelling…':
    '취소하는 중…',
  'Order cancelled. You can order that meal again before the cutoff.':
    '주문을 취소했습니다. 마감 전까지는 다시 신청할 수 있습니다.',
  'That order can no longer be cancelled':
    '이 주문은 더 이상 취소할 수 없습니다',
  'Ordering for that meal has closed — ask an admin for an exception':
    '이 식사는 신청이 마감됐습니다 — 예외가 필요하면 관리자에게 요청하세요',
  'Order not found':
    '주문을 찾을 수 없습니다',
  'Show earlier orders ({count})':
    '이전 주문 더 보기 ({count})',
  'Show fewer':
    '접기',
  'That order is no longer available.':
    '이 주문은 더 이상 볼 수 없습니다.',
  'You’re set for {meal}':
    '{meal} 신청 완료',
  'This order was cancelled':
    '취소된 주문입니다',
  'Pick up at {place}.':
    '{place}에서 받으세요.',
  'Your company admin will confirm the pickup point.':
    '받는 곳은 회사 관리자가 정해줍니다.',
  'Order no.':
    '주문 번호',
  'Meal':
    '식사',
  'Restaurant':
    '식당',
  'Pick up at':
    '받는 곳',
  'Not set':
    '정하지 않음',
  'Amount':
    '금액',
  'Changeable for another {left} — it closes {when}. After that the restaurant is cooking.':
    '{left} 동안 바꿀 수 있습니다 — {when}에 마감되고, 그 뒤로는 식당이 조리를 시작합니다.',
  'Closed at {time}. Ask your admin if you need an exception.':
    '{time}에 마감됐습니다. 예외가 필요하면 관리자에게 요청하세요.',
  'Order again':
    '다시 신청하기',
  'Back to home':
    '홈으로',
};

const ES: Record<string, string> = {
  Breakfast: 'Desayuno',
  Lunch: 'Comida',
  Dinner: 'Cena',
  Home: 'Inicio',
  Menu: 'Menú',
  Orders: 'Pedidos',
  Alerts: 'Avisos',
  'Hi, {name}': 'Hola, {name}',
  Hi: 'Hola',
  'Nothing open to order': 'No hay nada abierto para pedir',
  'Every published day has closed. The weekly menu shows what is coming.':
    'Todos los días publicados han cerrado. El menú semanal muestra lo que viene.',
  'Collecting today': 'Recoges hoy',
  'No pickup point set': 'Sin punto de recogida',
  'Order for {day}': 'Pedir para {day}',
  'Nothing to order': 'Nada que pedir',
  'No open days': 'No hay días abiertos',
  'Everything published has closed. The weekly menu shows what is coming.':
    'Todo lo publicado ha cerrado. El menú semanal muestra lo que viene.',
  'Whole week': 'Semana completa',
  'Booked ahead': 'Reservado',
  'left · closes {when}': 'restante · cierra {when}',
  Ordered: 'Pedido',
  'View or change': 'Ver o cambiar',
  'Order {meal}': 'Pedir {meal}',
  'Weekly menu': 'Menú semanal',
  'days published': 'días publicados',
  'This week': 'Esta semana',
  'Book several days': 'Reservar varios días',
  Cancel: 'Cancelar',
  'Tap the days you want. You choose where they go on the next screen — all together, or one day at a time.':
    'Marca los días que quieras. En la siguiente pantalla eliges dónde los recoges: todos juntos o uno a uno.',
  Open: 'Abierto',
  Closed: 'Cerrado',
  'Order by {when}': 'Pide antes de {when}',
  'Closed {when}': 'Cerró {when}',
  Continue: 'Continuar',
  'Nothing published yet': 'Nada publicado todavía',
  'Nothing is published for these days yet. Your restaurant posts the week when it is ready.':
    'No hay nada publicado para estos días. El restaurante publica la semana cuando está lista.',
  'Where do these go?': '¿Dónde los recoges?',
  'Every day': 'Todos los días',
  'Choose a pickup point': 'Elige un punto de recogida',
  'Nothing can be ordered until one is set': 'No se puede pedir sin un punto de recogida',
  Change: 'Cambiar',
  'The days you picked': 'Los días que elegiste',
  'No pickup point': 'Sin punto de recogida',
  'Back to the menu': 'Volver al menú',
  'Nothing picked': 'No has elegido nada',
  'Choose the days you want on the weekly menu, then set where they go.':
    'Elige los días en el menú semanal y luego indica dónde los recoges.',
  'Every day goes to': 'Todos los días a',
  'Sets all of them. A single day can still be changed on its own card.':
    'Se aplica a todos. Un día suelto se puede cambiar en su propia tarjeta.',
  'Just this day': 'Solo este día',
  'Only this meal moves. The rest stay where they are.':
    'Solo se mueve esta comida. El resto se queda donde está.',
  'set to somewhere else': 'día(s) en otro sitio',
  'See my orders': 'Ver mis pedidos',
  'These could not be ordered:': 'No se pudieron pedir:',
  'Your share': 'Tu parte',
  Billing: 'Importe',
  'Place order': 'Confirmar pedido',
  'This meal is closed': 'Esta comida está cerrada',
  Allergens: 'Alérgenos',
  'Pickup point': 'Punto de recogida',
  'My orders': 'Mis pedidos',
  'Next up': 'Lo siguiente',
  'Collect at {place}': 'Recoger en {place}',
  'No pickup point — set one in your profile': 'Sin punto de recogida: elígelo en tu perfil',
  'Nothing booked': 'Nada reservado',
  'Days you order appear here with what you ordered and where to collect it.':
    'Los días que pidas aparecen aquí con qué pediste y dónde recogerlo.',
  'Nothing yet this month': 'Nada este mes',
  'Meals you have eaten appear here.': 'Las comidas que ya has tenido aparecen aquí.',
  'company paid in full': 'paga la empresa',
  Confirmed: 'Confirmado',
  Delivered: 'Entregado',
  Cancelled: 'Cancelado',
  Exception: 'Excepción',
  'Mark all read': 'Marcar todo leído',
  'Nothing yet': 'Nada todavía',
  'Cutoff reminders and menu updates arrive here.':
    'Los avisos de cierre y de menú llegan aquí.',
  'Email is on by default. Push and SMS come in a later version.':
    'El correo está activado. Las notificaciones y los SMS llegarán más adelante.',
  'Where your meals are delivered. Changing it affects new orders — ones you have already placed keep the point they were made with.':
    'Donde se entregan tus comidas. El cambio afecta a los pedidos nuevos; los ya hechos mantienen su punto.',
  'No pickup points are set up for your site yet. Ask your company admin.':
    'Tu centro no tiene puntos de recogida. Pídeselo al administrador de tu empresa.',
  Password: 'Contraseña',
  'Change your password': 'Cambiar la contraseña',
  'Sign out': 'Cerrar sesión',
  Language: 'Idioma',
  'Sign in': 'Entrar',
  'Your work email and the password you chose. You stay signed in for 90 days.':
    'Tu correo de trabajo y la contraseña que elegiste. La sesión dura 90 días.',
  'Work email': 'Correo de trabajo',
  'First time here, or forgot your password?':
    '¿Primera vez, o has olvidado la contraseña?',
  'Only addresses your company registered can sign in. Guests get a temporary pass for their visit dates.':
    'Solo entran las direcciones que registró tu empresa. Las visitas reciben un pase temporal.',
  'Set your password': 'Establece tu contraseña',
  'We send a link to your work email. Following it proves the address is yours, and then you choose a password.':
    'Enviamos un enlace a tu correo. Abrirlo demuestra que la dirección es tuya y luego eliges contraseña.',
  'Send the link': 'Enviar el enlace',
  'Send another link': 'Enviar otro enlace',
  'Back to sign in': 'Volver a entrar',
  'Choose a password': 'Elige una contraseña',
  Again: 'Repítela',
  'New password': 'Nueva contraseña',
  'Save and sign in': 'Guardar y entrar',
  'Choose your own password': 'Elige tu propia contraseña',
  'You are signed in with a password somebody gave you. Replace it before carrying on.':
    'Has entrado con una contraseña que te dieron. Cámbiala antes de seguir.',
  'The password you were given': 'La contraseña que te dieron',
  'Save and sign in again': 'Guardar y entrar de nuevo',

  // Joining from the company link
  'This link no longer works':
    'Este enlace ya no funciona',
  'Your company may have replaced it. Ask whoever manages accounts there for the current one.':
    'Puede que tu empresa lo haya cambiado. Pide el enlace actual a quien gestiona las cuentas.',
  'Already have an account? Sign in':
    '¿Ya tienes cuenta? Entrar',
  'Request sent':
    'Solicitud enviada',
  '{company} will look at it. Once they approve it, sign in with the email and password you just chose.':
    '{company} la revisará. Cuando la aprueben, entra con el correo y la contraseña que acabas de elegir.',
  'Nothing else to do. If it is urgent, tell whoever manages accounts at your company that you have asked.':
    'No tienes que hacer nada más. Si es urgente, avisa a quien gestiona las cuentas de que lo has pedido.',
  'Could not sign you up':
    'No se pudo completar el registro',
  'Could not sign you in':
    'No se pudo entrar',
  'Join {company}':
    'Únete a {company}',
  'Join your company':
    'Únete a tu empresa',
  'Sign up and you can order straight away.':
    'Regístrate y podrás pedir enseguida.',
  'If your employee number and email match what your company has on file, you can order straight away. If not, your company approves you first.':
    'Si tu número de empleado y tu correo coinciden con los que tiene tu empresa, podrás pedir enseguida. Si no, tu empresa te aprueba primero.',
  'Employee number':
    'Número de empleado',
  'The number on your badge. Leave it blank if you do not have one.':
    'El número de tu gafete. Déjalo en blanco si no tienes.',
  'Email':
    'Correo',
  'The one your company has for you, if it has one.':
    'El que tiene tu empresa, si tiene uno.',
  'Name':
    'Nombre',
  'At least 10 characters. You stay signed in for 90 days.':
    'Al menos 10 caracteres. La sesión dura 90 días.',
  'Where do you pick up lunch?':
    '¿Dónde recoges la comida?',
  'Decide later':
    'Decidir después',
  'Join':
    'Unirme',
  'Your company has not approved this account yet. You can sign in as soon as it does.':
    'Tu empresa todavía no ha aprobado esta cuenta. Podrás entrar en cuanto lo haga.',
  'That email is already registered. Sign in, or ask whoever manages accounts at your company.':
    'Ese correo ya está registrado. Entra, o pregunta a quien gestiona las cuentas de tu empresa.',
  'This link is not valid':
    'Este enlace no es válido',
  'That pickup point is not one of this company’s':
    'Ese punto de recogida no es de esta empresa',
  'Password must be at least 10 characters':
    'La contraseña debe tener al menos 10 caracteres',

  // Order limits
  'Full':
    'Completo',
  '{count} left':
    'Quedan {count}',
  'The kitchen has taken all it can cook for this day.':
    'La cocina ya ha aceptado todo lo que puede preparar ese día.',
  'Replace order':
    'Cambiar pedido',

  // Cancelling, and the order screen
  'Cancel order':
    'Cancelar pedido',
  'Cancel this meal?':
    '¿Cancelar esta comida?',
  'You can order it again until {when}.':
    'Puedes volver a pedirla hasta {when}.',
  'Yes, cancel':
    'Sí, cancelar',
  'Keep it':
    'Mantener',
  'Cancelling…':
    'Cancelando…',
  'Order cancelled. You can order that meal again before the cutoff.':
    'Pedido cancelado. Puedes volver a pedir esa comida antes del cierre.',
  'That order can no longer be cancelled':
    'Este pedido ya no se puede cancelar',
  'Ordering for that meal has closed — ask an admin for an exception':
    'El plazo para esta comida ha cerrado — pide una excepción a un administrador',
  'Order not found':
    'No se encontró el pedido',
  'Show earlier orders ({count})':
    'Ver pedidos anteriores ({count})',
  'Show fewer':
    'Ver menos',
  'That order is no longer available.':
    'Este pedido ya no está disponible.',
  'You’re set for {meal}':
    'Tu {meal} está pedido',
  'This order was cancelled':
    'Este pedido se canceló',
  'Pick up at {place}.':
    'Recógelo en {place}.',
  'Your company admin will confirm the pickup point.':
    'El administrador de tu empresa confirmará el punto de recogida.',
  'Order no.':
    'N.º de pedido',
  'Meal':
    'Comida',
  'Restaurant':
    'Restaurante',
  'Pick up at':
    'Recoger en',
  'Not set':
    'Sin definir',
  'Amount':
    'Importe',
  'Changeable for another {left} — it closes {when}. After that the restaurant is cooking.':
    'Puedes cambiarlo durante {left} — cierra {when}. Después, la cocina ya está preparando.',
  'Closed at {time}. Ask your admin if you need an exception.':
    'Cerró a las {time}. Si necesitas una excepción, pídesela a tu administrador.',
  'Order again':
    'Pedir de nuevo',
  'Back to home':
    'Volver al inicio',
};

const DICTIONARIES: Record<Language, Record<string, string>> = { en: {}, ko: KO, es: ES };

function read(): Language {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'ko' || saved === 'es') return saved;
  } catch {
    // Private windows and blocked storage: fall through to the browser's own.
  }

  const first = navigator.languages?.[0] ?? navigator.language ?? 'en';
  if (first.startsWith('ko')) return 'ko';
  if (first.startsWith('es')) return 'es';
  return 'en';
}

let current: Language = read();
const listeners = new Set<() => void>();

export function setLanguage(language: Language): void {
  current = language;
  try {
    localStorage.setItem(KEY, language);
  } catch {
    // A viewer who cannot store it still gets it for this visit.
  }
  document.documentElement.lang = language;
  for (const listener of listeners) listener();
}

export function language(): Language {
  return current;
}

export function translate(text: string, values?: Record<string, string | number>): string {
  const dictionary = DICTIONARIES[current];
  let out = dictionary[text] ?? text;

  if (values) {
    for (const [key, value] of Object.entries(values)) {
      out = out.replaceAll(`{${key}}`, String(value));
    }
  }

  return out;
}

/** Re-renders the screens when the language changes, and nothing else. */
export function useT(): {
  t: (text: string, values?: Record<string, string | number>) => string;
  lang: Language;
} {
  const lang = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );

  return { t: translate, lang };
}
